import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarAlt,
  faClipboardCheck,
  faShip,
  faTruck,
  faDollarSign,
  faWarehouse,
  faUserClock,
  faBox,
  faFileAlt,
  faExclamationTriangle,
  faCheckCircle,
  faHourglass,
  faMapMarkerAlt,
  faFileInvoiceDollar,
  faHistory,
  faClock,
  faChartLine,
  faTools,
} from "@fortawesome/free-solid-svg-icons";

const TrafficTimeline = ({ item }) => {
  // Function to check if a date string has meaningful time (not midnight)
  const hasMeaningfulTime = (dateStr) => {
    if (!dateStr) return false;

    // For ISO dates with 'T' and 'Z'
    if (dateStr.includes("T") && dateStr.includes(".000Z")) {
      return false; // These are midnight times
    }

    // For ISO dates with 'T' but specific time
    if (dateStr.includes("T") && !dateStr.endsWith("00:00:00.000Z")) {
      return true;
    }

    // For dates with space separator and time
    if (dateStr.includes(" ") && dateStr.split(" ")[1]) {
      const timePart = dateStr.split(" ")[1];
      // Check if time is not 00:00:00 or 00:00
      return timePart !== "00:00:00" && timePart !== "00:00";
    }

    // Date-only format
    return false;
  };

  // Function to parse date string to Date object without timezone conversion
  const parseDate = (dateStr) => {
    if (!dateStr) return null;

    // For ISO format, parse directly
    if (dateStr.includes("T")) {
      return new Date(dateStr.replace("Z", ""));
    }

    // For space-separated format, replace space with T
    if (dateStr.includes(" ")) {
      return new Date(dateStr.replace(" ", "T"));
    }

    // Date only - parse at noon to avoid any issues
    return new Date(dateStr + "T12:00:00");
  };

  // Function to format date for display
  const formatDate = (date) => {
    if (!date) return "N/A";

    const dateObj = typeof date === "string" ? parseDate(date) : date;
    if (!dateObj || isNaN(dateObj.getTime())) return "N/A";

    const options = { year: "numeric", month: "short", day: "numeric" };
    return dateObj.toLocaleDateString(undefined, options);
  };

  // Function to format time for display
  const formatTime = (date) => {
    if (!date) return "";

    const dateObj = typeof date === "string" ? parseDate(date) : date;
    if (!dateObj || isNaN(dateObj.getTime())) return "";

    return dateObj.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Function to get formatted date with time if meaningful
  const getFormattedDateTime = (date) => {
    if (!date) return "N/A";

    // If it's a string, check if it has meaningful time
    if (typeof date === "string" && !hasMeaningfulTime(date)) {
      return formatDate(date);
    }

    // If it's a Date object or has meaningful time, show both
    return `${formatDate(date)} ${formatTime(date)}`;
  };

  // Function to get icon for status
  const getStatusIcon = (status) => {
    const statusIconMap = {
      "En Transito": { icon: faShip, color: "text-blue-500" },
      "Llegada al Puerto": { icon: faMapMarkerAlt, color: "text-green-500" },
      Descarga: { icon: faTruck, color: "text-yellow-500" },
      "En Almacen": { icon: faWarehouse, color: "text-purple-500" },
      "Despacho Aduanal": { icon: faFileAlt, color: "text-red-500" },
      Modulacion: { icon: faClipboardCheck, color: "text-indigo-500" },
      Pagado: { icon: faDollarSign, color: "text-green-600" },
      Carga: { icon: faBox, color: "text-orange-500" },
      Entregado: { icon: faCheckCircle, color: "text-green-700" },
      "En Espera": { icon: faHourglass, color: "text-gray-500" },
      Programado: { icon: faUserClock, color: "text-cyan-500" },
      Facturado: { icon: faFileInvoiceDollar, color: "text-emerald-600" },
      "En Revision": { icon: faTools, color: "text-amber-500" },
      Retenido: { icon: faExclamationTriangle, color: "text-red-600" },
      // Default for unknown statuses
      default: { icon: faHistory, color: "text-gray-500" },
    };

    return status && statusIconMap[status]
      ? statusIconMap[status]
      : statusIconMap["default"];
  };

  // Function to get icon for booking dates
  const getEbookingDateIcon = (dateType) => {
    const dateIconMap = {
      bookingCode: { icon: faFileAlt, color: "text-blue-600" },
      terminal: { icon: faWarehouse, color: "text-indigo-500" },
      fechaGlosa: { icon: faFileAlt, color: "text-purple-500" },
      fechaAlta: { icon: faCalendarAlt, color: "text-green-500" },
      fechaETA: { icon: faShip, color: "text-blue-500" },
      fechaPiso: { icon: faWarehouse, color: "text-amber-500" },
      fechaAtraco: { icon: faShip, color: "text-teal-500" },
      fechaCita: { icon: faUserClock, color: "text-cyan-600" },
      fechaReprogramacion: { icon: faUserClock, color: "text-red-500" },
      fechaRevalidacion: { icon: faClipboardCheck, color: "text-green-600" },
      costoAlmacenaje: { icon: faDollarSign, color: "text-green-700" },
      // Default
      default: { icon: faClock, color: "text-gray-500" },
    };

    return dateIconMap[dateType] || dateIconMap["default"];
  };

  // Function to get human-readable name for ebooking date types
  const getEbookingDateName = (dateType) => {
    const dateNameMap = {
      bookingCode: "Booking Code",
      terminal: "Terminal Assignment",
      fechaGlosa: "Glose Date",
      fechaAlta: "Registration Date",
      fechaETA: "ETA Date",
      fechaPiso: "Floor Date",
      fechaAtraco: "Docking Date",
      fechaCita: "Appointment Date",
      fechaReprogramacion: "Rescheduling Date",
      fechaRevalidacion: "Revalidation Date",
      costoAlmacenaje: "Storage Cost",
    };

    return dateNameMap[dateType] || dateType;
  };

  // Combine all timeline events from different sources
  const buildTimelineEvents = () => {
    let events = [];

    // Add events from operation dates
    if (item.header.detail?.dates_data) {
      const datesData = item.header.detail.dates_data;

      if (datesData.date_init) {
        const date = parseDate(datesData.date_init);
        if (date && !isNaN(date.getTime())) {
          events.push({
            date,
            originalDateStr: datesData.date_init,
            event: "Traffic Created",
            icon: { icon: faFileAlt, color: "text-blue-500" },
            source: "Traffic System",
          });
        }
      }

      if (datesData.date_inbound) {
        const date = parseDate(datesData.date_inbound);
        if (date && !isNaN(date.getTime())) {
          events.push({
            date,
            originalDateStr: datesData.date_inbound,
            event: "Inbound",
            icon: { icon: faShip, color: "text-green-500" },
            source: "Traffic System",
          });
        }
      }

      if (datesData.date_crossing) {
        const date = parseDate(datesData.date_crossing);
        if (date && !isNaN(date.getTime())) {
          events.push({
            date,
            originalDateStr: datesData.date_crossing,
            event: "Crossing",
            icon: { icon: faTruck, color: "text-purple-500" },
            source: "Traffic System",
          });
        }
      }

      if (datesData.date_pedimento_paid) {
        const date = parseDate(datesData.date_pedimento_paid);
        if (date && !isNaN(date.getTime())) {
          events.push({
            date,
            originalDateStr: datesData.date_pedimento_paid,
            event: "Pedimento Paid",
            icon: { icon: faDollarSign, color: "text-yellow-500" },
            source: "Traffic System",
          });
        }
      }

      if (datesData.date_end) {
        const date = parseDate(datesData.date_end);
        if (date && !isNaN(date.getTime())) {
          events.push({
            date,
            originalDateStr: datesData.date_end,
            event: "Completed",
            icon: { icon: faCheckCircle, color: "text-green-500" },
            source: "Traffic System",
          });
        }
      }
    }

    // Add events from booking statuses
    if (
      item.header.trafficStatusTimeline &&
      item.header.trafficStatusTimeline.length > 0
    ) {
      item.header.trafficStatusTimeline.forEach((statusItem) => {
        events.push({
          date: new Date(statusItem.fechaSatus),
          event: statusItem.status,
          icon: getStatusIcon(statusItem.status),
          source: "Booking System",
          updatedBy: statusItem.actualizadoPor,
        });
      });
    }

    // Add events from ebooking dates
    if (item.header.ebookingDates) {
      Object.entries(item.header.ebookingDates).forEach(([key, value]) => {
        // Skip null values and non-date fields
        if (
          !value ||
          key === "bookingCode" ||
          key === "terminal" ||
          key === "costoAlmacenaje"
        ) {
          return;
        }

        events.push({
          date: new Date(value),
          event: getEbookingDateName(key),
          icon: getEbookingDateIcon(key),
          source: "E-Booking System",
        });
      });
    }

    // Sort all events by date
    return events.sort((a, b) => a.date - b.date);
  };

  // Meta information display (top card - non-date information)
  const renderMetaInformation = () => {
    const ebookingDates = item.header.ebookingDates || {};

    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <h3 className="text-sm font-medium text-gray-900 mb-3 border-b pb-2 flex items-center">
          <FontAwesomeIcon icon={faChartLine} className="mr-2 text-blue-500" />
          Traffic Meta Information
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {ebookingDates.bookingCode && (
            <div className="p-3 bg-blue-50 rounded-lg">
              <div className="flex items-center">
                <div className="mr-3">
                  <FontAwesomeIcon
                    icon={faFileAlt}
                    className="text-blue-600 text-lg"
                  />
                </div>
                <div>
                  <div className="text-xs text-gray-500">Booking Code</div>
                  <div className="font-medium">{ebookingDates.bookingCode}</div>
                </div>
              </div>
            </div>
          )}

          {ebookingDates.terminal && (
            <div className="p-3 bg-indigo-50 rounded-lg">
              <div className="flex items-center">
                <div className="mr-3">
                  <FontAwesomeIcon
                    icon={faWarehouse}
                    className="text-indigo-600 text-lg"
                  />
                </div>
                <div>
                  <div className="text-xs text-gray-500">Terminal</div>
                  <div className="font-medium">{ebookingDates.terminal}</div>
                </div>
              </div>
            </div>
          )}

          {ebookingDates.costoAlmacenaje && (
            <div className="p-3 bg-green-50 rounded-lg">
              <div className="flex items-center">
                <div className="mr-3">
                  <FontAwesomeIcon
                    icon={faDollarSign}
                    className="text-green-600 text-lg"
                  />
                </div>
                <div>
                  <div className="text-xs text-gray-500">Storage Cost</div>
                  <div className="font-medium">
                    {typeof ebookingDates.costoAlmacenaje === "object"
                      ? JSON.stringify(ebookingDates.costoAlmacenaje)
                      : ebookingDates.costoAlmacenaje}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const timelineEvents = buildTimelineEvents();

  return (
    <div className="space-y-4">
      {/* Meta Information Card */}
      {renderMetaInformation()}

      {/* Timeline */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <h3 className="text-sm font-medium text-gray-900 mb-3 border-b pb-2 flex items-center">
          <FontAwesomeIcon
            icon={faCalendarAlt}
            className="mr-2 text-blue-500"
          />
          Traffic Timeline
        </h3>

        {timelineEvents.length > 0 ? (
          <div className="relative pl-8">
            {/* Timeline line */}
            <div className="absolute top-0 bottom-0 left-4 w-0.5 bg-blue-200"></div>

            {/* Timeline events */}
            {timelineEvents.map((event, index) => (
              <div key={index} className="mb-6 relative">
                {/* Timeline dot */}
                <div
                  className={`absolute -left-4 mt-1.5 w-2.5 h-2.5 rounded-full border-2 border-white ${event.icon.color.replace(
                    "text-",
                    "bg-"
                  )}`}
                ></div>

                <div className="text-sm">
                  <div className="text-xs text-gray-500 mb-1 flex items-center justify-between">
                    <span>
                      {getFormattedDateTime(
                        event.originalDateStr || event.date
                      )}
                    </span>
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs ${
                        event.source === "Traffic System"
                          ? "bg-blue-100 text-blue-800"
                          : event.source === "Booking System"
                          ? "bg-purple-100 text-purple-800"
                          : "bg-green-100 text-green-800"
                      }`}
                    >
                      {event.source}
                    </span>
                  </div>
                  <div className="font-medium text-gray-900 flex items-center">
                    <FontAwesomeIcon
                      icon={event.icon.icon}
                      className={`mr-2 ${event.icon.color}`}
                    />
                    {event.event}
                  </div>
                  {event.updatedBy && (
                    <div className="text-xs text-gray-500 mt-1 italic">
                      Updated by: {event.updatedBy}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-gray-500 text-sm italic text-center py-4">
            No timeline events available
          </div>
        )}
      </div>

      {/* Date Information Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <h3 className="text-sm font-medium text-gray-900 mb-3 border-b pb-2 flex items-center">
          <FontAwesomeIcon
            icon={faCalendarAlt}
            className="mr-2 text-blue-500"
          />
          All Dates Information
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* System Dates */}
          <div className="border rounded-lg p-4">
            <h4 className="font-medium text-sm mb-3 pb-1 border-b text-blue-600 flex items-center">
              <FontAwesomeIcon icon={faFileAlt} className="mr-2" />
              Traffic System Dates
            </h4>
            <div className="space-y-2">
              {item.header.detail?.dates_data?.date_init && (
                <div className="flex justify-between">
                  <span className="text-gray-500 text-sm">Created Date:</span>
                  <span className="text-sm">
                    {formatDate(item.header.detail.dates_data.date_init)}
                  </span>
                </div>
              )}
              {item.header.detail?.dates_data?.date_inbound && (
                <div className="flex justify-between">
                  <span className="text-gray-500 text-sm">Inbound Date:</span>
                  <span className="text-sm">
                    {formatDate(item.header.detail.dates_data.date_inbound)}
                  </span>
                </div>
              )}
              {item.header.detail?.dates_data?.date_crossing && (
                <div className="flex justify-between">
                  <span className="text-gray-500 text-sm">Crossing Date:</span>
                  <span className="text-sm">
                    {formatDate(item.header.detail.dates_data.date_crossing)}
                  </span>
                </div>
              )}
              {item.header.detail?.dates_data?.date_pedimento_paid && (
                <div className="flex justify-between">
                  <span className="text-gray-500 text-sm">
                    Pedimento Paid Date:
                  </span>
                  <span className="text-sm">
                    {formatDate(
                      item.header.detail.dates_data.date_pedimento_paid
                    )}
                  </span>
                </div>
              )}
              {item.header.detail?.dates_data?.date_end && (
                <div className="flex justify-between">
                  <span className="text-gray-500 text-sm">End Date:</span>
                  <span className="text-sm">
                    {formatDate(item.header.detail.dates_data.date_end)}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* E-Booking Dates */}
          <div className="border rounded-lg p-4">
            <h4 className="font-medium text-sm mb-3 pb-1 border-b text-green-600 flex items-center">
              <FontAwesomeIcon icon={faShip} className="mr-2" />
              E-Booking System Dates
            </h4>
            <div className="space-y-2">
              {item.header.ebookingDates &&
                Object.entries(item.header.ebookingDates)
                  .filter(
                    ([key, value]) =>
                      value &&
                      key !== "bookingCode" &&
                      key !== "terminal" &&
                      key !== "costoAlmacenaje"
                  )
                  .map(([key, value], index) => (
                    <div key={index} className="flex justify-between">
                      <span className="text-gray-500 text-sm">
                        {getEbookingDateName(key)}:
                      </span>
                      <span className="text-sm">{formatDate(value)}</span>
                    </div>
                  ))}
              {(!item.header.ebookingDates ||
                Object.entries(item.header.ebookingDates).filter(
                  ([key, value]) =>
                    value &&
                    key !== "bookingCode" &&
                    key !== "terminal" &&
                    key !== "costoAlmacenaje"
                ).length === 0) && (
                <div className="text-center text-sm text-gray-500 italic py-2">
                  No e-booking dates available
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrafficTimeline;
